/**
 * Searchable column combobox (`createColumnCombobox`).
 * A flat text input with a filterable dropdown of columns, keyboard nav
 * (↑/↓/Enter/Esc), and type badges. Shared infra: the filter builder uses it
 * now; derived signals (§10.4) and the plot pickers (§10.7) reuse it later.
 *
 * The committed value is owned by the parent via the `value` property + the
 * `select` callback - typing only filters the menu; a pick (click or Enter)
 * commits. This mirrors the blob, where free-typed text never changed the
 * selection until an item was chosen.
 */

import { html, nothing, type PropertyValues, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LightElement } from '../lit-base.ts';
import { isNumericType, friendlyType, wrapColName } from '../utils/columns.ts';
import { coverageLabel, type PoolColumn } from '../features/ship/ship-columns.ts';
import { fuzzyScore } from '../utils/fuzzy.ts';
import type { ColumnInfo } from '../api/types.ts';

@customElement('column-combobox')
export class ColumnCombobox extends LightElement {
  /**
   * Pool of selectable columns (already filtered/sorted by the parent). Accepts
   * `PoolColumn`, so a multi-ship pool carries its coverage badge through - a
   * plain `ColumnInfo[]` still assigns, since coverage is optional.
   */
  @property({ attribute: false }) columns: PoolColumn[] = [];
  /** Committed column name. */
  @property() value = '';
  @property() placeholder = 'Search columns…';
  /**
   * Clear the input back to empty after a pick instead of showing the chosen
   * name. Used by the multi-add pickers (series / histogram), where the choice
   * is surfaced as a chip and the box should reset for the next add. Without
   * this, the always-`''` `value` binding is dirty-checked unchanged by Lit, so
   * the picked name would linger in the box.
   */
  @property({ type: Boolean }) clearOnPick = false;
  /**
   * Anchor the dropdown to the input's right edge instead of its left. Used
   * when the box sits near a container's right edge (e.g. the second derived
   * operand), so the wider-than-input menu grows leftward and stays on-screen.
   */
  @property({ type: Boolean }) alignRight = false;
  /**
   * Greys the box out and blocks picking. Used where the control is real but
   * unavailable in the current mode, so it reads as deliberately off rather
   * than silently ignoring clicks.
   */
  @property({ type: Boolean }) disabled = false;
  /** Called with the chosen column name when the user picks one. */
  @property({ attribute: false }) select: (name: string) => void = () => {};
  /**
   * When set, a × appears at the right of the input while a value is committed
   * and calls this. The parent decides what "cleared" means - the line X picker
   * drops back to the time axis rather than to nothing, since a plot with no x
   * column is not a request the backend accepts.
   */
  @property({ attribute: false }) clear: (() => void) | null = null;

  /** Text shown in the input - diverges from `value` only while typing. */
  @state() private draft = '';
  @state() private open = false;
  @state() private highlighted = -1;

  override willUpdate(changed: PropertyValues<this>): void {
    // Keep the input in sync with the committed value unless the user is
    // mid-edit (menu open), so external changes (URL restore, prune) show.
    if (changed.has('value') && !this.open) this.draft = this.value;
  }

  private visibleColumns(): ColumnInfo[] {
    const q = this.draft.trim();
    if (!q) return this.columns;
    // Fuzzy-filter, then rank best matches first so the top item (highlighted
    // by default) is the closest hit. Stable within equal scores.
    const scored: Array<{ c: ColumnInfo; score: number }> = [];
    for (const c of this.columns) {
      const score = fuzzyScore(q, c.name);
      if (score !== null) scored.push({ c, score });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.c);
  }

  private openMenu(): void {
    this.open = true;
    this.highlighted = this.visibleColumns().length ? 0 : -1;
  }

  private pick(name: string): void {
    this.open = false;
    this.highlighted = -1;
    if (this.clearOnPick) {
      // Reset to placeholder; the parent surfaces the choice elsewhere (a chip).
      this.draft = '';
    } else {
      this.draft = name;
      if (name !== this.value) this.value = name;
    }
    this.select(name);
  }

  private onInput(e: Event): void {
    this.draft = (e.target as HTMLInputElement).value;
    this.open = true;
    this.highlighted = this.visibleColumns().length ? 0 : -1;
  }

  private onKeydown(e: KeyboardEvent): void {
    if (!this.open) {
      if (e.key !== 'Escape') this.openMenu();
      return;
    }
    const vis = this.visibleColumns();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this.highlighted = Math.min(vis.length - 1, this.highlighted + 1);
      this.scrollToHighlighted();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this.highlighted = Math.max(0, this.highlighted - 1);
      this.scrollToHighlighted();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const c = vis[this.highlighted];
      if (c) this.pick(c.name);
    } else if (e.key === 'Escape') {
      this.open = false;
      (e.target as HTMLInputElement).blur();
    }
  }

  private scrollToHighlighted(): void {
    void this.updateComplete.then(() => {
      const items = this.querySelectorAll<HTMLElement>('.combobox-item');
      items[this.highlighted]?.scrollIntoView({ block: 'nearest' });
    });
  }

  override render(): TemplateResult {
    const vis = this.visibleColumns();
    const clearable = !!this.clear && !!this.value && !this.disabled;
    return html`
      <div
        class="combobox ${this.open ? 'open' : ''} ${this.alignRight ? 'align-right' : ''} ${clearable
          ? 'has-clear'
          : ''}"
      >
        <input
          type="text"
          autocomplete="off"
          spellcheck="false"
          ?disabled=${this.disabled}
          placeholder=${this.placeholder}
          .value=${this.draft}
          @focus=${this.openMenu}
          @input=${this.onInput}
          @blur=${() => setTimeout(() => (this.open = false), 150)}
          @keydown=${this.onKeydown}
        />
        <div class="combobox-menu">
          ${vis.length === 0
            ? html`<div class="combobox-empty">No matches</div>`
            : vis.map(
                (c, i) => html`
                  <div
                    class="combobox-item ${i === this.highlighted ? 'active' : ''}"
                    @mousedown=${(e: Event) => {
                      e.preventDefault();
                      this.pick(c.name);
                    }}
                    @mouseenter=${() => {
                      this.highlighted = i;
                    }}
                  >
                    <span class="col-name-text">${wrapColName(c.name)}</span>
                    ${coverageLabel(c)
                      ? html`<span class="coverage-badge" title=${`Only some selected ships report ${c.name}`}
                          >${coverageLabel(c)}</span
                        >`
                      : nothing}
                    <span class="type-badge ${isNumericType(c.type) ? 'numeric' : 'string'}"
                      >${friendlyType(c.type)}</span
                    >
                  </div>
                `,
              )}
        </div>
        ${clearable
          ? html`<button
              class="combobox-clear"
              type="button"
              title="Clear"
              aria-label="Clear"
              @mousedown=${(e: Event) => {
                // mousedown, not click: the input's blur handler closes the menu
                // on a delay and would swallow the click.
                e.preventDefault();
                e.stopPropagation();
                this.draft = '';
                this.open = false;
                this.clear?.();
              }}
            >
              ×
            </button>`
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'column-combobox': ColumnCombobox;
  }
}
